import type { Buyer, Faq } from '../types/product';

export const CHECKOUT_URL = 'https://ecg-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/giNb1j42t5iefTbbZQ49Wo/MOCKUP_ECG-EN.png',
  rachel: 'https://cdn.magicpatterns.com/uploads/8m5zQdL2ZiA5WTa79Jyoyo/Rachel_Bennett_-_NURSING.png',
};

export const collectionIncludes = [
  '6 Comprehensive Clinical Education Volumes',
  '5 Specialized ECG Learning Bonuses',
  'Visual Rhythm Recognition Charts',
  'Step-by-Step ECG Interpretation Worksheets',
  '12-Lead ECG Educational References',
  'Simulated Cardiac Case Studies',
  'Practical Knowledge Checklists',
];

export const before = [
  'You memorize cardiac rhythms without fully understanding their underlying characteristics.',
  'You struggle to distinguish similar-looking ECG patterns.',
  'You repeatedly search for explanations of PR intervals, QRS duration, and ST-segment changes.',
  'You find 12-lead ECG interpretation overwhelming because there are so many concepts to consider.',
  'You review practice questions but don’t always understand the reasoning behind the answers.',
  'You have ECG screenshots, lecture notes, and reference charts scattered across different resources.',
  'You want to improve your understanding but aren’t sure which concepts to review first.',
];

export const after = [
  'You have a structured educational sequence for reviewing ECG fundamentals.',
  'You can study P waves, QRS complexes, T waves, and intervals through organized visual references.',
  'You can compare common cardiac rhythms using dedicated interpretation charts.',
  'You have a consistent framework for working through ECG learning exercises.',
  'You can explore 12-lead ECG concepts using visual diagrams and guided examples.',
  'You can practice with simulated clinical scenarios and review detailed explanations.',
  'You have worksheets, quick-reference tools, and checklists to organize your study sessions.',
];

export const situations = [
  {
    title: 'You Can Recognize Individual ECG Waves, But Connecting Them Is Difficult',
    body: [
      'You know the basic terminology.',
      'However, understanding how heart rate, rhythm, intervals, and waveform characteristics relate to one another can still feel confusing.',
      'You want a structured way to study those relationships.',
    ],
  },
  {
    title: 'Different Cardiac Rhythms Look Too Similar',
    body: [
      'Sinus rhythms, atrial rhythms, junctional rhythms, and ventricular rhythms can have overlapping characteristics.',
      'You want comparison charts that help you understand their distinguishing features.',
    ],
  },
  {
    title: 'You Feel Overwhelmed by 12-Lead ECG Interpretation',
    body: [
      'The different leads, cardiac territories, axis concepts, and ST-segment patterns introduce another level of complexity.',
      'You want to approach these topics in a logical educational sequence.',
    ],
  },
  {
    title: 'You Keep Forgetting ECG Intervals and Measurements',
    body: [
      'You review the same measurements repeatedly.',
      'But without a consistent reference system, remembering how those measurements fit into the broader ECG picture becomes challenging.',
    ],
  },
  {
    title: 'You Want More Practice With Realistic ECG Examples',
    body: [
      'Reading definitions is helpful.',
      'But you also want simulated cases that encourage you to examine a tracing, organize your observations, and explain your reasoning.',
    ],
  },
  {
    title: 'You Have Too Many Disconnected ECG Resources',
    body: [
      'Lecture slides. Screenshots. Saved PDFs. Rhythm charts. Textbooks.',
      'You want one organized collection that helps you study without constantly searching for the next explanation.',
    ],
  },
];

export const skills = [
  'Understanding cardiac electrical activity.',
  'Recognizing waveform characteristics.',
  'Examining intervals and measurements.',
  'Comparing rhythm patterns.',
  'Understanding the information provided by different ECG leads.',
  'And considering the clinical context surrounding the tracing.',
];

export const tools = [
  {
    title: 'The ECG Interpretation Worksheet',
    body: 'A reusable template for organizing heart rate, rhythm regularity, waveforms, intervals, and other observations during practice exercises.',
  },
  {
    title: 'The Cardiac Rhythm Comparison Matrix',
    body: 'A visual table for comparing common cardiac rhythms and their distinguishing characteristics.',
  },
  {
    title: 'The 12-Lead ECG Study Map',
    body: 'An educational diagram showing lead orientation, anatomical relationships, and essential terminology.',
  },
  {
    title: 'The ECG Measurements Reference Sheet',
    body: 'A concise study reference covering common ECG measurements, terminology, and important limitations.',
  },
  {
    title: 'The ECG Case Review Checklist',
    body: 'A structured checklist for reviewing simulated cases and identifying areas for further learning.',
  },
];

export const valueStack = [
  { label: 'ECG Interpretation Blueprint™ — 6 Educational Volumes', value: 162 },
  { label: 'Bonus #1 — ECG Quick-Reference Cards', value: 17 },
  { label: 'Bonus #2 — 15-Minute ECG Study Planner', value: 12 },
  { label: 'Bonus #3 — Cardiac Arrhythmia Visual Comparison Atlas', value: 17 },
  { label: 'Bonus #4 — 12-Lead ECG Practice Expansion Pack', value: 17 },
  { label: 'Bonus #5 — ECG Interpretation Pitfalls Library', value: 12 },
];

export const purchaseIncludes = [
  'Six Comprehensive ECG Educational Volumes',
  'Five Specialized Learning Bonuses',
  'Visual Rhythm Recognition Charts',
  'Step-by-Step Interpretation Worksheets',
  '12-Lead ECG Educational References',
  'Simulated Clinical Practice Cases',
  'ECG Reference Tables and Study Checklists',
  'Digital Access to Your Purchased Materials',
  'No Monthly Subscription',
];

export const finalIncludes = [
  '6 Comprehensive ECG Educational Volumes',
  '5 Specialized ECG Study Bonuses',
  'Cardiac Rhythm Recognition Charts',
  '12-Lead ECG Educational References',
  'ECG Interpretation Worksheets',
  'Visual Comparison Tables',
  'Simulated Clinical Case Exercises',
  'Structured Study Checklists',
];

export const faqs: Faq[] = [
  {
    question: 'How will I receive the ECG Interpretation Blueprint™?',
    answer: [
      'After your payment is confirmed, you’ll receive instructions for accessing your digital materials through the delivery method configured at checkout.',
    ],
  },
  {
    question: 'Is this collection suitable for nursing students?',
    answer: [
      'Yes. The collection is designed to support educational study, beginning with ECG fundamentals and progressing toward more complex interpretation concepts.',
      'Nursing students can use the materials to complement their formal education.',
    ],
  },
  {
    question: 'Is it suitable for experienced nurses?',
    answer: [
      'Yes. Registered nurses, emergency nurses, and critical care professionals may find the reference charts, comparison tables, and simulated cases useful for reviewing ECG concepts.',
      'The material does not replace competency assessments or continuing education requirements.',
    ],
  },
  {
    question: 'Do I need previous ECG interpretation experience?',
    answer: [
      'No advanced experience is required to begin studying the foundational volumes.',
      'However, more complex sections may require additional learning, professional instruction, and supervised practice.',
    ],
  },
  {
    question: 'Does the blueprint cover both cardiac rhythms and 12-lead ECGs?',
    answer: [
      'Yes. The collection includes educational content about common cardiac rhythms, ECG waveforms, intervals, conduction concepts, and introductory 12-lead ECG interpretation.',
      'It does not claim to cover every possible ECG abnormality or clinical situation.',
    ],
  },
  {
    question: 'Can I use this resource to make patient-care decisions?',
    answer: [
      'No. This is an educational reference collection.',
      'It is not a validated clinical decision algorithm and does not replace qualified clinical assessment, professional judgment, institutional protocols, or applicable guidelines.',
    ],
  },
  {
    question: 'Is this collection available internationally?',
    answer: [
      'The materials are written in English and may be useful to nursing students and professionals in different countries. Your local currency is converted automatically at checkout.',
      'However, clinical responsibilities, terminology, standards, and professional requirements vary by location. Always follow the applicable guidance and regulations in your jurisdiction.',
    ],
  },
  {
    question: 'Are the materials downloadable?',
    answer: ['The collection consists of digital educational files.'],
  },
  {
    question: 'Is this a one-time payment?',
    answer: [
      'Yes. The ECG Interpretation Blueprint™ is offered for a one-time payment of $27 USD.',
      'There is no monthly subscription associated with this purchase.',
    ],
  },
  {
    question: 'Will I receive future updates?',
    answer: [
      'Your purchase includes the digital materials described in the offer.',
      'Any future updates or additional content will depend on the terms communicated at checkout. No future updates are guaranteed unless explicitly included in the purchase terms.',
    ],
  },
  {
    question: 'What happens if the product doesn’t meet my expectations?',
    answer: [
      'Please review the refund policy presented at checkout before purchasing.',
      'If a seven-day satisfaction guarantee applies, the published policy will explain the applicable conditions and refund request process.',
    ],
  },
  {
    question: 'Does this product provide continuing education credits or professional certification?',
    answer: [
      'No continuing education credits or professional certification are included unless separately confirmed through an authorized accreditation process.',
      'The collection is intended for independent educational study and professional reference.',
    ],
  },
];

export const buyers: Buyer[] = [
  { name: 'Valentina R.', city: 'Bogotá', country: 'Colombia' },
  { name: 'Camila G.', city: 'Guadalajara', country: 'México' },
  { name: 'Lucía F.', city: 'Madrid', country: 'España' },
  { name: 'Mariana P.', city: 'Lima', country: 'Perú' },
  { name: 'Sofía M.', city: 'Buenos Aires', country: 'Argentina' },
  { name: 'Daniela C.', city: 'Santiago', country: 'Chile' },
  { name: 'Andrea V.', city: 'Quito', country: 'Ecuador' },
  { name: 'Gabriela H.', city: 'San José', country: 'Costa Rica' },
  { name: 'José L.', city: 'Montevideo', country: 'Uruguay' },
  { name: 'Carlos M.', city: 'Caracas', country: 'Venezuela' },
  { name: 'Isabel T.', city: 'Asunción', country: 'Paraguay' },
  { name: 'Fernanda D.', city: 'Santo Domingo', country: 'República Dominicana' },
  { name: 'Paola S.', city: 'Ciudad de Panamá', country: 'Panamá' },
  { name: 'Ana R.', city: 'La Paz', country: 'Bolivia' },
  { name: 'Luis A.', city: 'San Juan', country: 'Puerto Rico' },
  { name: 'Rocío B.', city: 'Sevilla', country: 'España' },
  { name: 'Natalia O.', city: 'Medellín', country: 'Colombia' },
  { name: 'Jimena E.', city: 'Monterrey', country: 'México' },
];
